import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { UserRole } from '../../../../core/enums/users/role';
import { ApplicationState } from '../../../../core/models/application-state/application-state';
import { AuthenticatedUser } from '../../../../core/models/authenticated-user';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { ApplicationService } from '../../shared/services/application/application.service';
import { CoordinationIndustrialBusinessSubmitToEwaComponent } from './components/coordination-industrial-business-submit-to-ewa/coordination-industrial-business-submit-to-ewa.component';
import { MinimumEwaHandoverSubmitToEwaComponent } from './components/minimum-ewa-handover-submit-to-ewa/minimum-ewa-handover-submit-to-ewa.component';
import { PublicSafetyCustomerSubmitToEwaComponent } from './components/public-safety-customer-submit-to-ewa/public-safety-customer-submit-to-ewa.component';
import { PublicSafetyEwaSubmitToEwaComponent } from './components/public-safety-ewa-submit-to-ewa/public-safety-ewa-submit-to-ewa.component';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { takeUntil, switchMap, Subject } from 'rxjs';
import { ApplicationPurposeCodeEnum } from '../../shared/constants/application-purpose-codes';
import { RenewalOnlyAndNoticeOfConstructionSubmitToEwaComponent } from './components/renewal-only-and-notice-of-construction-submit-to-ewa/renewal-only-and-notice-of-construction-submit-to-ewa.component';
import { AdvisorIndustrialBusinessSubmitToEwaComponent } from './components/advisor-industrial-business-submit-to-ewa/advisor-industrial-business-submit-to-ewa.component';
import { AdvisorPublicSafetySubmitToEwaComponent } from './components/advisor-public-safety-submit-to-ewa/advisor-public-safety-submit-to-ewa.component';
import { SubmitToEwaConstants } from './constants/submit-to-ewa-constants';
import { ApplicationTypeEnum } from '../../../../core/constants/application-type-enum';

@Component({
    selector: 'app-submit-to-ewa',
    templateUrl: './submit-to-ewa.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MinimumEwaHandoverSubmitToEwaComponent,
        CoordinationIndustrialBusinessSubmitToEwaComponent,
        PublicSafetyCustomerSubmitToEwaComponent,
        PublicSafetyEwaSubmitToEwaComponent,
        RenewalOnlyAndNoticeOfConstructionSubmitToEwaComponent,
        AdvisorIndustrialBusinessSubmitToEwaComponent,
        AdvisorPublicSafetySubmitToEwaComponent,
    ],
})
export class SubmitToEwaComponent implements OnInit, OnDestroy {
    /* Used to determine which component to display based on the application state and user roles */
    applicationState: ApplicationState | null = null;
    user: AuthenticatedUser | null = null;
    allStepsValid: boolean | null = null;

    applicationId: string | undefined;
    destroy$ = new Subject<void>();

    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly userStateService = inject(UserStateService);
    private readonly applicationService = inject(ApplicationService);
    private readonly route = inject(ActivatedRoute);

    ngOnInit(): void {
        this.initializeDisplayDependencies();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Determines whether the minimum EWA handover submit to ewa should be displayed.
     */
    displayMinimumEwaHandover(): boolean {
        return (
            !this.displayAdvisorSimpleSubmitToEwa() &&
            !this.displayAdvisorPublicSafetySubmitToEwa() &&
            this.allStepsValid === false &&
            !!this.applicationState?.applicationPurpose &&
            SubmitToEwaConstants.allowedPurposesForMinimumHandover.includes(
                this.applicationState.applicationPurpose,
            )
        );
    }

    /**
     * Determines whether the simple advisor submit to ewa final page should be displayed.
     * This page is shown for all I/B applications created on behalf of a customer by EWA staff,
     * and for public safety RO/NT applications regardless of step validation status.
     */
    displayAdvisorSimpleSubmitToEwa(): boolean {
        return (
            !!this.applicationState?.isCreatedOnBehalfOfCustomer &&
            this.user?.role !== UserRole.Customer &&
            !!this.applicationState?.applicationPurpose &&
            (this.isAdvisorIndustrialBusinessSimpleHandoverPurpose() ||
                this.isAdvisorPublicSafetySimpleHandoverPurpose())
        );
    }

    /**
     * Determines whether the advisor public safety submit to ewa final page should be displayed.
     * This page is shown for public safety applications created on behalf of a customer by EWA staff,
     * regardless of step validation status.
     */
    displayAdvisorPublicSafetySubmitToEwa(): boolean {
        return (
            !!this.applicationState?.isCreatedOnBehalfOfCustomer &&
            this.user?.role !== UserRole.Customer &&
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.PublicSafety &&
            !!this.applicationState?.applicationPurpose &&
            SubmitToEwaConstants.allowedPurposesForAdvisorPublicSafetyHandover.includes(
                this.applicationState.applicationPurpose,
            )
        );
    }

    /**
     * Determines whether the public safety customer submit to ewa should be displayed.
     */
    displayPublicSafetyCustomer(): boolean {
        return (
            !!this.allStepsValid &&
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.PublicSafety &&
            this.user?.role === UserRole.Customer &&
            !!this.applicationState?.applicationPurpose &&
            SubmitToEwaConstants.allowedPurposesForPublicSafetyCustomerHandover.includes(
                this.applicationState.applicationPurpose,
            )
        );
    }

    /**
     * Determines whether the public safety EWA submit to ewa should be displayed.
     */
    displayPublicSafetyEwa(): boolean {
        return (
            !this.displayAdvisorPublicSafetySubmitToEwa() &&
            !!this.allStepsValid &&
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.PublicSafety &&
            this.user?.role !== UserRole.Customer &&
            !!this.applicationState?.applicationPurpose &&
            SubmitToEwaConstants.allowedPurposesForPublicSafetyEwaHandover.includes(
                this.applicationState.applicationPurpose,
            )
        );
    }

    /**
     * Determines whether the coordination industrial business submit to ewa should be displayed.
     */
    displayCoordinationIndustrialBusiness(): boolean {
        return (
            !this.displayAdvisorSimpleSubmitToEwa() &&
            !!this.allStepsValid &&
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.IndustrialBusiness &&
            !!this.applicationState?.applicationPurpose &&
            SubmitToEwaConstants.allowedPurposesForCoordinationIndustrialBusinessHandover.includes(
                this.applicationState.applicationPurpose,
            )
        );
    }

    /**
     * Determines whether the renewal only and notice of construction submit to ewa final page should be displayed.
     */
    displayRenewalOnlyAndNoticeOfConstruction(): boolean {
        return (
            !this.displayAdvisorSimpleSubmitToEwa() &&
            (this.applicationState?.applicationPurpose ===
                ApplicationPurposeCodeEnum.renewalOnly ||
                this.applicationState?.applicationPurpose ===
                    ApplicationPurposeCodeEnum.constructionNotification)
        );
    }

    private initializeDisplayDependencies(): void {
        this.grabApplicationId();
        this.loadApplicationState();
        this.subscribeToAuthenticatedUser();
        this.loadApplicationStepsValid();
    }

    /**
     * Loads the result of whether all application steps are passing.
     */
    private loadApplicationStepsValid(): void {
        this.applicationService
            .getAllStepsValid(this.applicationId!)
            .subscribe((allStepsValid) => {
                this.allStepsValid = allStepsValid;
            });
    }

    /**
     * Loads the application state from the application state service.
     */
    private loadApplicationState(): void {
        this.applicationStateService.applicationState$.subscribe(
            (applicationState) => {
                this.applicationState = applicationState;
            },
        );
    }

    /**
     * Subscribes to the authenticated user from the user state service.
     */
    private subscribeToAuthenticatedUser(): void {
        this.userStateService.user$.subscribe((user) => {
            this.user = user;
        });
    }

    /**
     * Grabs the application ID from the route.
     */
    private grabApplicationId(): void {
        this.route
            .parent!.paramMap.pipe(
                takeUntil(this.destroy$),
                switchMap((params: ParamMap) => {
                    return (this.applicationId = params.get('id')!);
                }),
            )
            .subscribe();
    }

    private isAdvisorIndustrialBusinessSimpleHandoverPurpose(): boolean {
        return (
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.IndustrialBusiness &&
            SubmitToEwaConstants.allowedPurposesForAdvisorIndustrialBusinessHandover.includes(
                this.applicationState.applicationPurpose!,
            )
        );
    }

    private isAdvisorPublicSafetySimpleHandoverPurpose(): boolean {
        return (
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.PublicSafety &&
            SubmitToEwaConstants.allowedPurposesForAdvisorPublicSafetySimpleHandover.includes(
                this.applicationState.applicationPurpose!,
            )
        );
    }
}
